import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Target,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  DollarSign,
  BarChart3,
  Activity,
  Clock,
  AlertTriangle,
  CheckCircle,
  Phone,
  Mail,
  MessageSquare
} from "lucide-react";

// Mock data - substituir com dados reais da API
const mockConversionData = {
  overview: {
    totalBudgets: 156,
    converted: 107,
    lost: 49,
    conversionRate: 68.5,
    averageTime: 12,
    totalValue: 2450000,
    convertedValue: 1820000,
    followUpRate: 45.2
  },
  funnel: [
    { stage: "Enviado", count: 156, percentage: 100, color: "bg-blue-500" },
    { stage: "Em Negociação", count: 128, percentage: 82, color: "bg-yellow-500" },
    { stage: "Follow-up Realizado", count: 95, percentage: 61, color: "bg-purple-500" },
    { stage: "Proposta Ajustada", count: 87, percentage: 56, color: "bg-orange-500" },
    { stage: "Fechado", count: 107, percentage: 68.5, color: "bg-green-500" }
  ],
  monthlyConversion: [
    { month: "Jan", sent: 12, converted: 8, rate: 66.7, time: 14 },
    { month: "Fev", sent: 15, converted: 11, rate: 73.3, time: 11 },
    { month: "Mar", sent: 18, converted: 13, rate: 72.2, time: 10 },
    { month: "Abr", sent: 14, converted: 9, rate: 64.3, time: 15 },
    { month: "Mai", sent: 16, converted: 12, rate: 75.0, time: 9 },
    { month: "Jun", sent: 20, converted: 15, rate: 75.0, time: 8 }
  ],
  conversionFactors: [
    { factor: "Preço Competitivo", impact: 85, positive: true },
    { factor: "Relacionamento com Cliente", impact: 78, positive: true },
    { factor: "Tempo de Resposta", impact: 72, positive: true },
    { factor: "Follow-up Frequente", impact: 65, positive: true },
    { factor: "Proposta Personalizada", impact: 82, positive: true },
    { factor: "Concorrência Alta", impact: 45, positive: false },
    { factor: "Complexidade Técnica", impact: 38, positive: false }
  ],
  lostReasons: [
    { reason: "Preço Alto", count: 18, percentage: 36.7 },
    { reason: "Concorrente Melhor", count: 12, percentage: 24.5 },
    { reason: "Timing Incorreto", count: 8, percentage: 16.3 },
    { reason: "Não Atende Necessidade", count: 6, percentage: 12.2 },
    { reason: "Sem Resposta", count: 5, percentage: 10.2 }
  ],
  bestPractices: [
    {
      title: "Contato em 24h",
      description: "Clientes contatados em até 24h têm 35% mais chance de conversão",
      impact: "+35%",
      difficulty: "Baixa"
    },
    {
      title: "Mínimo 3 Follow-ups",
      description: "Orçamentos com 3+ follow-ups convertem 2x mais",
      impact: "+100%",
      difficulty: "Média"
    },
    {
      title: "Proposta Personalizada",
      description: "Adaptar a proposta às necessidades específicas aumenta a conversão",
      impact: "+42%",
      difficulty: "Alta"
    },
    {
      title: "Visita Técnica",
      description: "Visitas prévias aumentam a confiança e a taxa de aprovação",
      impact: "+28%",
      difficulty: "Média"
    }
  ]
};

export default function BudgetConversionPage() {
  const [timeRange, setTimeRange] = useState("6m");
  const [selectedStage, setSelectedStage] = useState("all");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Análise de Conversão</h1>
          <p className="text-muted-foreground">
            Taxas de conversão, fatores de sucesso e oportunidades de melhoria
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Último mês</SelectItem>
              <SelectItem value="3m">Últimos 3 meses</SelectItem>
              <SelectItem value="6m">Últimos 6 meses</SelectItem>
              <SelectItem value="1y">Último ano</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockConversionData.overview.conversionRate}%</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">+5.2%</span>
              <span>vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockConversionData.overview.averageTime} dias</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingDown className="h-3 w-3 text-green-500" />
              <span className="text-green-500">-3 dias</span>
              <span>vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Follow-up</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockConversionData.overview.followUpRate}%</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">+8.1%</span>
              <span>vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Convertido</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(mockConversionData.overview.convertedValue / 1000000).toFixed(1)}M
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">+12.3%</span>
              <span>vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Oportunidades</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockConversionData.overview.lost}</div>
            <div className="text-xs text-muted-foreground">
              Orçamentos perdidos
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="funnel" className="space-y-4">
        <TabsList>
          <TabsTrigger value="funnel">Funil de Vendas</TabsTrigger>
          <TabsTrigger value="factors">Fatores de Sucesso</TabsTrigger>
          <TabsTrigger value="analysis">Análise de Perdas</TabsTrigger>
          <TabsTrigger value="practices">Melhores Práticas</TabsTrigger>
        </TabsList>

        {/* Sales Funnel */}
        <TabsContent value="funnel" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Funil de Conversão
                </CardTitle>
                <CardDescription>
                  Progresso dos orçamentos através das etapas de venda
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockConversionData.funnel.map((stage, index) => (
                  <div key={stage.stage} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                        <span className="font-medium">{stage.stage}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{stage.count} orçamentos</div>
                        <div className="text-sm text-muted-foreground">
                          {stage.percentage}% do total
                        </div>
                      </div>
                    </div>
                    <Progress value={stage.percentage} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Evolução Mensal
                </CardTitle>
                <CardDescription>
                  Taxa de conversão e tempo de fechamento por mês
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mockConversionData.monthlyConversion.map((month) => (
                    <div key={month.month} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{month.month}</div>
                        <div className="text-sm text-muted-foreground">
                          {month.converted}/{month.sent} convertidos
                        </div>
                      </div>
                      <div className="text-center">
                        <Badge variant={month.rate >= 70 ? "default" : "secondary"}>
                          {month.rate}%
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          Taxa de conversão
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{month.time}d</div>
                        <div className="text-xs text-muted-foreground">
                          Tempo médio
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Success Factors */}
        <TabsContent value="factors" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Fatores de Impacto na Conversão
                </CardTitle>
                <CardDescription>
                  Elementos que mais influenciam o sucesso dos orçamentos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockConversionData.conversionFactors.map((factor) => (
                  <div key={factor.factor} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{factor.factor}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          factor.positive ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {factor.impact}%
                        </span>
                        {factor.positive ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>
                    <Progress value={factor.impact} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Principais Motivos de Perda
                </CardTitle>
                <CardDescription>
                  Razões mais comuns para não conversão
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockConversionData.lostReasons.map((reason) => (
                  <div key={reason.reason} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{reason.reason}</div>
                      <div className="text-sm text-muted-foreground">
                        {reason.count} orçamentos
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="secondary">{reason.percentage}%</Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        do total perdido
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Loss Analysis */}
        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Análise Detalhada de Perdas
              </CardTitle>
              <CardDescription>
                Compreenda os padrões e oportunidades nos orçamentos perdidos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-3">
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold text-red-600 mb-2">36.7%</div>
                  <div className="font-medium mb-1">Preço Alto</div>
                  <div className="text-sm text-muted-foreground">
                    Principal motivo de perda
                  </div>
                </div>
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600 mb-2">61.1%</div>
                  <div className="font-medium mb-1">Preço + Concorrência</div>
                  <div className="text-sm text-muted-foreground">
                    Combinados representam a maioria
                  </div>
                </div>
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">R$ 630K</div>
                  <div className="font-medium mb-1">Valor Perdido</div>
                  <div className="text-sm text-muted-foreground">
                    Oportunidade mensal
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">Oportunidade Identificada</h4>
                    <p className="text-sm text-blue-800">
                      67% das perdas estão relacionadas a preço e concorrência.
                      Considere revisar a estratégia de precificação e diferencial competitivo.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Best Practices */}
        <TabsContent value="practices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Melhores Práticas para Aumentar Conversão
              </CardTitle>
              <CardDescription>
                Ações comprovadas que melhoram a taxa de sucesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {mockConversionData.bestPractices.map((practice, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 rounded-full text-sm font-bold">
                          {index + 1}
                        </div>
                        <h3 className="font-semibold">{practice.title}</h3>
                      </div>
                      <Badge variant="outline" className="text-green-600">
                        {practice.impact}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {practice.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Dificuldade: {practice.difficulty}
                      </span>
                      <Button size="sm" variant="outline">
                        Implementar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-900 mb-1">Recomendação Prioritária</h4>
                    <p className="text-sm text-green-800">
                      Comece com "Contato em 24h" e "Mínimo 3 Follow-ups" -
                      são práticas de baixa/média dificuldade com alto impacto.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}